import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, Variants } from 'framer-motion';
import { MoreHorizontal, Frown, RefreshCw } from 'lucide-react';
import { colors } from '../styles/colors';
import { typography } from '../styles/typography';
import { spacing } from '../styles/spacing';
import { memoryService, Memory } from '../services/MemoryService';
import { authService } from '../services/AuthService';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Loader from './ui/Loader';

const PageContainer = styled.div`
  padding: ${spacing[4]};
  padding-bottom: 120px; /* For nav bar */
`;

const Header = styled.header`
  padding: ${spacing[8]} 0 ${spacing[6]};
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: ${typography.sizes['4xl']};
  font-weight: ${typography.weights.bold};
  color: ${colors.text.primary};
  margin: 0;
  letter-spacing: ${typography.letterSpacing.tight};
`;

const Subtitle = styled(motion.p)`
  color: ${colors.text.secondary};
  margin-top: ${spacing[2]};
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${colors.border.primary};
  }
  & span {
    padding: 0 ${spacing[4]};
    color: ${colors.text.secondary};
    font-size: ${typography.sizes.sm};
    font-weight: ${typography.weights.medium};
  }
`;

const ErrorContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  background: ${colors.background.primary};
  padding: ${spacing[4]};
`;

const IconWrapper = styled(motion.div)`
  margin-bottom: ${spacing[6]};
`;

const MemoryFeed = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[8]};
  max-width: 500px;
  margin: 0 auto;
`;

const MemoryCardContainer = styled(motion.div)<{ isCurrentUser?: boolean }>`
  border-radius: ${spacing.radius['2xl']};
  overflow: hidden;
  box-shadow: ${colors.shadows.lg};
  background: ${({ isCurrentUser }) =>
    isCurrentUser ? colors.background.primary : colors.gradients.primary};
  color: ${({ isCurrentUser }) => (isCurrentUser ? 'inherit' : colors.text.white)};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 90%;
  margin-left: ${({ isCurrentUser }) => (isCurrentUser ? 'auto' : '0')};
  margin-right: ${({ isCurrentUser }) => (isCurrentUser ? '0' : 'auto')};
  border-bottom-left-radius: ${({ isCurrentUser }) => (isCurrentUser ? spacing.radius['2xl'] : '4px')};
  border-bottom-right-radius: ${({ isCurrentUser }) => (isCurrentUser ? '4px' : spacing.radius['2xl'])};


  &:hover {
    transform: translateY(-4px);
    box-shadow: ${colors.shadows.xl};
  }
`;

const highlightAnimation = `
  @keyframes highlight {
    0% { box-shadow: 0 0 20px 5px ${colors.primary.light}; }
    50% { box-shadow: 0 0 30px 10px ${colors.primary.light}; }
    100% { box-shadow: ${colors.shadows.lg}; }
  }
`;

const MemoryCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
`;

const MemoryUserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  line-height: 1;
`;

const MemoryUserInfo = styled.div`
  flex: 1;
`;

const MemoryUserName = styled.h3`
  font-size: ${typography.sizes.base};
  font-weight: ${typography.weights.semibold};
  color: ${colors.text.primary};
`;

const MemoryTimestamp = styled.p`
  font-size: ${typography.sizes.sm};
  color: ${colors.text.muted};
`;

const InvertedTextColor = styled.div`
  h2, h3 {
    color: ${colors.text.white};
  }
  p {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const MemoryCardImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  aspect-ratio: 1 / 1;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
`;

const MemoryCardContent = styled.div`
  padding: ${spacing[4]};
`;

const MemoryCardTitle = styled.h2`
  font-size: ${typography.sizes.lg};
  font-weight: ${typography.weights.semibold};
  margin: 0 0 ${spacing[2]};
`;

const MemoryCardText = styled.p`
  color: ${colors.text.secondary};
`;

const EnlargedImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${spacing.radius.lg};
  margin-bottom: ${spacing[4]};
`;

const SenderAttribution = styled.p`
  color: ${colors.text.secondary};
  font-size: ${typography.sizes.sm};
  margin-bottom: ${spacing[2]};
`;

interface MemoryWallProps {
  highlightNewest?: boolean;
  onHighlightComplete?: () => void;
}

const MemoryWall: React.FC<MemoryWallProps> = ({ highlightNewest, onHighlightComplete }) => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);
  const currentUser = authService.getCurrentUser();
  const [highlightedMemoryId, setHighlightedMemoryId] = useState<number | null>(null);

  const getUserDetails = (userId: string | undefined) => {
    // Determine user based on a persistent ID, not a changeable name
    if (userId === 'user1') { // Assuming 'user1' is the ID for chikimoon from the backend
      return { name: 'Chikimoon', avatar: '🌕' };
    }
    if (userId === 'user2') { // Assuming 'user2' is the ID for ishoun from the backend
      return { name: 'Ishoun', avatar: '🐼' };
    }
    // Fallback for any other case
    return { name: 'Love', avatar: '❤️' };
  };

  useEffect(() => {
    const fetchMemories = async () => {
      try {
        setIsLoading(true);
        const fetchedMemories = await memoryService.getMemories();
        setMemories(fetchedMemories);
        setError(null);

        if (highlightNewest && fetchedMemories.length > 0) {
          const newestMemory = fetchedMemories[0];
          setHighlightedMemoryId(newestMemory.id);

          setTimeout(() => {
            setHighlightedMemoryId(null);
            onHighlightComplete?.();
          }, 5000); // Highlight for 5 seconds
        }

      } catch (err) {
        setError('Failed to load memories. Please try again later.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemories();
  }, [highlightNewest, onHighlightComplete]);

  const groupedMemories = memories.reduce((acc, memory) => {
    // Ensure the date string is parsed as UTC by adding 'Z'
    const date = new Date(memory.created_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(memory);
    return acc;
  }, {} as Record<string, Memory[]>);

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Loader text="Loading memories..." />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    const containerVariants: Variants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.2,
        },
      },
    };

    const itemVariants: Variants = {
      hidden: { y: 20, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          type: 'spring',
          stiffness: 100,
        },
      },
    };

    return (
      <ErrorContainer
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <IconWrapper
          variants={itemVariants}
        >
          <Frown size={64} color={colors.primary.DEFAULT} />
        </IconWrapper>
        <Title variants={itemVariants}>Oops!</Title>
        <Subtitle variants={itemVariants}>{error}</Subtitle>
        <motion.div variants={itemVariants} style={{ marginTop: spacing[4] }}>
          <Button
            onClick={() => window.location.reload()}
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw size={16} />}
          >
            Try Again
          </Button>
        </motion.div>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>Memory Garden</Title>
        <Subtitle>A timeline of our shared moments.</Subtitle>
      </Header>

      <MemoryFeed>
        {Object.entries(groupedMemories).map(([date, memoriesForDay]) => (
          <React.Fragment key={date}>
            <DateSeparator>
              <span>{date}</span>
            </DateSeparator>
            {memoriesForDay.map((memory) => (
              <MemoryCardContainer
                key={memory.id}
                isCurrentUser={memory.user_id === currentUser?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                onClick={() => setSelectedMemory(memory)}
                style={{
                  animation: highlightedMemoryId === memory.id ? 'highlight 2s ease-out' : 'none'
                }}
              >
                {memory.user_id !== currentUser?.id ? (
                  <InvertedTextColor>
                    <MemoryCardHeader>
                      <MemoryUserAvatar>{getUserDetails(memory.user_id).avatar}</MemoryUserAvatar>
                      <MemoryUserInfo>
                        <MemoryUserName>{getUserDetails(memory.user_id).name}</MemoryUserName>
                        <MemoryTimestamp>{new Date(memory.created_at.replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</MemoryTimestamp>
                      </MemoryUserInfo>
                      <MoreHorizontal size={20} color={colors.text.muted} />
                    </MemoryCardHeader>
                  </InvertedTextColor>
                ) : (
                  <MemoryCardHeader>
                    <MemoryUserAvatar>{getUserDetails(memory.user_id).avatar}</MemoryUserAvatar>
                    <MemoryUserInfo>
                      <MemoryUserName>{getUserDetails(memory.user_id).name}</MemoryUserName>
                      <MemoryTimestamp>{new Date(memory.created_at.replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</MemoryTimestamp>
                    </MemoryUserInfo>
                    <MoreHorizontal size={20} color={colors.text.muted} />
                  </MemoryCardHeader>
                )}

                {memory.type === 'photo' && memory.image_url && (
                  <MemoryCardImage imageUrl={memory.image_url} />
                )}
                
                <MemoryCardContent>
                  {memory.user_id !== currentUser?.id ? (
                    <InvertedTextColor>
                      <MemoryCardTitle>{memory.title}</MemoryCardTitle>
                      <MemoryCardText>{memory.content}</MemoryCardText>
                    </InvertedTextColor>
                  ) : (
                    <>
                      <MemoryCardTitle>{memory.title}</MemoryCardTitle>
                      <MemoryCardText>{memory.content}</MemoryCardText>
                    </>
                  )}
                </MemoryCardContent>
              </MemoryCardContainer>
            ))}
          </React.Fragment>
        ))}
      </MemoryFeed>

      <Modal 
        isOpen={!!selectedMemory} 
        onClose={() => setSelectedMemory(null)}
        title={selectedMemory ? new Date(selectedMemory.created_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB') : undefined}
      >
        {selectedMemory && (
          <div>
            <SenderAttribution>
              {`${
                getUserDetails(selectedMemory.user_id).name === 'Chikimoon' 
                  ? 'Chiko' 
                  : 'Ishoun'
              } sent -`}
            </SenderAttribution>
            {selectedMemory.type === 'photo' && selectedMemory.image_url && (
              <EnlargedImage src={selectedMemory.image_url} alt={selectedMemory.title} />
            )}
            <h2>{selectedMemory.title}</h2>
            <p>{selectedMemory.content}</p>
          </div>
        )}
      </Modal>
      <style>{highlightAnimation}</style>
    </PageContainer>
  );
};

export default MemoryWall;